import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "convex/_generated/api";
import { useApiMutation } from "@/packages/features-shared";
import { useTranslation } from "@/packages/utils";
import {
  NOTIFICATION_TYPES,
  type NotificationTypeConfig,
  type NotificationTypeKey,
  type BooleanFieldConfig,
  type NumberArrayFieldConfig,
} from "@/packages/types";
import { ClipboardCheck, FileWarning } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { SettingsPageHeader, SettingsPanel } from "./SettingsSection";
import { Toggle } from "./Toggle";

// ── Icon mapping ────────────────────────────────────────────────────────
// Maps icon string identifiers from NOTIFICATION_TYPES config to lucide components.
// Add new icons here as new notification types are added to the registry.
const ICON_MAP: Record<string, LucideIcon> = {
  FileWarning,
  ClipboardCheck,
};

function getIcon(iconName: string): LucideIcon | undefined {
  return ICON_MAP[iconName];
}

// ── TimingOptions sub-component ─────────────────────────────────────────

interface TimingOptionsProps {
  label: string;
  values: number[];
  validation: NumberArrayFieldConfig["validation"];
  disabled: boolean;
  onChange: (newValues: number[]) => void;
}

function TimingOptions({
  label,
  values,
  validation,
  disabled,
  onChange,
}: TimingOptionsProps) {
  const { t } = useTranslation("settings");
  // Generate available timing options from validation constraints.
  // Use the default values from the config as the available options,
  // since they represent the meaningful timing intervals.
  const availableOptions = generateTimingOptions(validation, (days) =>
    days === 0
      ? t("notifications.timing.onDay")
      : days === 1
        ? t("notifications.timing.oneDayBefore")
        : t("notifications.timing.daysBefore", { count: days }),
  );

  function handleToggle(days: number) {
    const newValues = values.includes(days)
      ? values.filter((d) => d !== days)
      : [...values, days].sort((a, b) => b - a);
    onChange(newValues);
  }

  return (
    <div className="border-t border-border pt-4">
      <p className="text-[12px] font-medium text-primary mb-3">{label}:</p>
      <div className="space-y-2">
        {availableOptions.map(({ value, label: optionLabel }) => (
          <label
            key={value}
            className="flex items-center gap-3 cursor-pointer group"
          >
            <input
              type="checkbox"
              checked={values.includes(value)}
              onChange={() => handleToggle(value)}
              disabled={disabled}
              className="w-4 h-4 rounded border-border text-accent accent-accent cursor-pointer"
            />
            <span className="text-[13px] text-primary group-hover:text-primary/80">
              {optionLabel}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}

/**
 * Generate the available timing options based on common expiration intervals.
 * Uses [0, 7, 14, 30, 60, 90] filtered by the validation constraints.
 */
function generateTimingOptions(
  validation: NumberArrayFieldConfig["validation"],
  getLabel: (days: number) => string,
): Array<{ value: number; label: string }> {
  const commonIntervals = [0, 7, 14, 30, 60, 90];
  return commonIntervals
    .filter((v) => v >= validation.min && v <= validation.maxValue)
    .sort((a, b) => b - a)
    .map((v) => ({
      value: v,
      label: getLabel(v),
    }));
}

// ── BooleanPreference sub-component ─────────────────────────────────────

interface BooleanPreferenceProps {
  prefKey: string;
  config: BooleanFieldConfig;
  value: boolean;
  disabled: boolean;
  onChange: (newValue: boolean) => void;
}

function BooleanPreference({
  prefKey,
  config,
  value,
  disabled,
  onChange,
}: BooleanPreferenceProps) {
  const { t } = useTranslation("settings");
  const label = t(`notifications.preferences.${prefKey}`, config.label);
  return (
    <div className="flex items-center justify-between">
      <span className="text-[13px] text-primary">{label}</span>
      <Toggle
        checked={value}
        onChange={() => onChange(!value)}
        disabled={disabled}
      />
    </div>
  );
}

// ── NotificationTypeSettings sub-component ──────────────────────────────

interface NotificationTypeSettingsProps {
  typeKey: NotificationTypeKey;
  config: NotificationTypeConfig;
  userPrefs: Record<string, unknown> | undefined;
  isLoading: boolean;
  onUpdate: (updates: Record<string, unknown>) => void;
}

function NotificationTypeSettings({
  typeKey,
  config,
  userPrefs,
  isLoading,
  onUpdate,
}: NotificationTypeSettingsProps) {
  const { t } = useTranslation("settings");
  const Icon = getIcon(config.icon);

  // Determine if this type is enabled (check the "enabled" preference if it exists)
  const enabledPref = config.preferences.enabled;
  const isEnabled =
    enabledPref && enabledPref.type === "boolean"
      ? ((userPrefs?.enabled ?? enabledPref.default) as boolean)
      : true;

  return (
    <div className="border border-border rounded-card p-4 space-y-4">
      {/* Header with icon, label, and description */}
      <div className="flex items-start gap-3">
        {Icon && (
          <div className="mt-0.5 text-secondary">
            <Icon className="w-4 h-4" />
          </div>
        )}
        <div>
          <h3 className="text-[13px] font-semibold text-primary">
            {t(`notifications.types.${typeKey}.label`)}
          </h3>
          <p className="text-[12px] text-secondary mt-0.5">
            {t(`notifications.types.${typeKey}.description`)}
          </p>
        </div>
      </div>

      {/* Boolean preferences (toggles) */}
      <div className="border-t border-border pt-4 space-y-3">
        {Object.entries(config.preferences)
          .filter(
            (entry): entry is [string, BooleanFieldConfig] =>
              entry[1].type === "boolean",
          )
          .map(([prefKey, prefConfig]) => (
            <BooleanPreference
              key={prefKey}
              prefKey={prefKey}
              config={prefConfig}
              value={(userPrefs?.[prefKey] ?? prefConfig.default) as boolean}
              disabled={isLoading}
              onChange={(newValue) => onUpdate({ [prefKey]: newValue })}
            />
          ))}
      </div>

      {/* NumberArray preferences (timing options) - only shown when type is enabled */}
      {isEnabled &&
        Object.entries(config.preferences)
          .filter(
            (entry): entry is [string, NumberArrayFieldConfig] =>
              entry[1].type === "numberArray",
          )
          .map(([prefKey, prefConfig]) => (
            <TimingOptions
              key={prefKey}
              label={prefConfig.label}
              values={(userPrefs?.[prefKey] ?? prefConfig.default) as number[]}
              validation={prefConfig.validation}
              disabled={isLoading}
              onChange={(newValues) => onUpdate({ [prefKey]: newValues })}
            />
          ))}
    </div>
  );
}

// ── Main NotificationsSection ───────────────────────────────────────────

export function NotificationsSection() {
  const { t } = useTranslation("settings");
  const { data: preferences, isLoading } = useQuery(
    convexQuery(api.notifications.queries.index.getPreferences, {}),
  );

  const updatePreferencesMutation = useApiMutation(
    api.notifications.mutations.index.updatePreferences,
    {
      name: "update-notification-preferences",
      silentSuccess: true,
    },
  );

  function handleUpdate(
    typeKey: NotificationTypeKey,
    updates: Record<string, unknown>,
  ) {
    updatePreferencesMutation.mutate({
      preferences: {
        [typeKey]: updates,
      },
    });
  }

  return (
    <div className="space-y-8">
      <SettingsPageHeader
        eyebrow={t("sections.account")}
        title={t("notifications.title")}
        description={t("notifications.description")}
      />
      <SettingsPanel>
        <div className="space-y-6">
          {Object.entries(NOTIFICATION_TYPES).map(([typeKey, config]) => (
            <NotificationTypeSettings
              key={typeKey}
              typeKey={typeKey as NotificationTypeKey}
              config={config}
              userPrefs={
                preferences?.[typeKey as keyof typeof preferences] as
                  | Record<string, unknown>
                  | undefined
              }
              isLoading={isLoading}
              onUpdate={(updates) =>
                handleUpdate(typeKey as NotificationTypeKey, updates)
              }
            />
          ))}
        </div>
      </SettingsPanel>
    </div>
  );
}
